import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface WizardStepIndicatorProps {
  currentStep: number;  // 0-3
  totalSteps: number;   // 4
}

export function WizardStepIndicator({ currentStep, totalSteps }: WizardStepIndicatorProps) {
  const { t } = useTranslation('financial-model');
  const STEP_LABELS = [
    t('wizard.step.typeAndBase'),
    t('wizard.step.revenue'),
    t('wizard.step.expenses'),
    t('wizard.step.overview'),
  ];

  return (
    <div className="flex items-start justify-between w-full">
      {Array.from({ length: totalSteps }, (_, i) => {
        const isCompleted = i < currentStep;
        const isCurrent = i === currentStep;
        const isFuture = i > currentStep;

        return (
          <div key={i} className="flex flex-col items-center flex-1 relative">
            {/* Connector line */}
            {i < totalSteps - 1 && (
              <div
                className={cn(
                  'absolute top-[16px] left-[calc(50%+16px)] right-[calc(-50%+16px)] h-[2px]',
                  isCompleted ? 'bg-[#2197f1]' : 'bg-[#eeeeee]'
                )}
              />
            )}

            {/* Circle */}
            <div
              className={cn(
                'w-[32px] h-[32px] rounded-full flex items-center justify-center relative z-10 shrink-0',
                (isCompleted || isCurrent) && 'bg-[#2197f1]',
                isFuture && 'bg-white border-[2px] border-[#eeeeee]'
              )}
            >
              {isCompleted ? (
                <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
                  <path
                    d="M1 5L5 9L13 1"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <span
                  className={cn(
                    "font-segoe text-[14px] font-semibold leading-[20px]",
                    isCurrent && 'text-white',
                    isFuture && 'text-[#969696]'
                  )}
                >
                  {i + 1}
                </span>
              )}
            </div>

            {/* Label */}
            <span
              className={cn(
                "font-segoe text-[12px] leading-[16px] mt-[6px] text-center",
                (isCompleted || isCurrent) && 'text-[#2197f1] font-semibold',
                isFuture && 'text-[#969696]'
              )}
            >
              {STEP_LABELS[i]}
            </span>
          </div>
        );
      })}
    </div>
  );
}
